import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import GroupsIcon from '@mui/icons-material/Groups';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import EditIcon from '@mui/icons-material/Edit';
import { useUsersAdminTable } from '../hooks/useUsersAdminTable';
import { PROFESIONES, ROLES, getProfesionLabel, getRoleLabel } from '../model/userConstants';
import { usePermissions } from '../../../shared/auth/PermissionsContext';
import UserPermissionsDialog from './UserPermissionsDialog';
import UserFormDialog from './UserFormDialog';

const ROLE_CHIP_COLORS = {
  SUPERADMIN: 'error',
  ADMIN: 'primary',
  SANITARIO: 'default',
};

const ROLE_AVATAR_COLORS = {
  SUPERADMIN: 'error.main',
  ADMIN: 'primary.main',
  SANITARIO: 'grey.500',
};

const getInitials = (nombre) => (nombre || '')
  .split(' ')
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase())
  .join('') || '?';

const STAT_CARDS = [
  { key: 'total', label: 'Usuarios totales', icon: GroupsIcon, color: '#0D6AEE' },
  { key: 'active', label: 'Activos', icon: HowToRegIcon, color: '#2e7d32' },
  { key: 'inactive', label: 'Inactivos', icon: PersonOffIcon, color: '#757575' },
  { key: 'admins', label: 'Administradores', icon: AdminPanelSettingsIcon, color: '#c2185b' },
];

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid #e3e8ef',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        height: '100%',
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${color}1a`,
          color,
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.1 }}>{value}</Typography>
        <Typography variant="caption" sx={{ color: '#6b7280' }}>{label}</Typography>
      </Box>
    </Paper>
  );
}

const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };

export default function AdminUsersTable() {
  const {
    rows, loading, stats,
    filterId, setFilterId, filterNombre, setFilterNombre, filterProfesional, setFilterProfesional,
    filterRole, setFilterRole, filterActive, setFilterActive,
    sortDate, setSortDate, resetFilters, toggleUserActive, fetchUsers,
  } = useUsersAdminTable();
  const { hasPermission } = usePermissions();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const canToggleActive = hasPermission('users:activate');
  const canManagePermissions = hasPermission('users:manage_permissions');
  const canCreateUsers = hasPermission('users:create');
  const canEditUsers = hasPermission('users:update');
  const hasActions = canToggleActive || canManagePermissions || canEditUsers;
  const formOpen = createOpen || !!editUser;
  const closeForm = () => { setCreateOpen(false); setEditUser(null); };

  useEffect(() => {
    setPage(0);
  }, [filterId, filterNombre, filterProfesional, filterRole, filterActive, sortDate]);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>Usuarios del sistema</Typography>
          <Typography sx={{ mt: 0.5, color: '#6b7280' }}>
            Consulta el equipo dado de alta, filtra por rol o estado y gestiona accesos y permisos.
          </Typography>
        </Box>
        {canCreateUsers ? (
          <Button
            variant="contained"
            onClick={() => setCreateOpen(true)}
            startIcon={<PersonAddAlt1Icon />}
            sx={{ px: 3, py: 1.25, borderRadius: 2 }}
          >
            Crear usuario
          </Button>
        ) : null}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {STAT_CARDS.map((card) => (
          <Grid item xs={6} md={3} key={card.key}>
            <StatCard icon={card.icon} label={card.label} value={stats[card.key]} color={card.color} />
          </Grid>
        ))}
      </Grid>

      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #e3e8ef', mb: 3 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size="small"
              fullWidth
              label="Buscar por nombre"
              value={filterNombre}
              onChange={(e) => setFilterNombre(e.target.value)}
              sx={inputSx}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField size="small" fullWidth label="ID" value={filterId} onChange={(e) => setFilterId(e.target.value)} sx={inputSx} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={filterProfesional}
              onChange={(e) => setFilterProfesional(e.target.value)}
              sx={inputSx}
            >
              <MenuItem value="">Toda profesión</MenuItem>
              {Object.entries(PROFESIONES).map(([label, value]) => (
                <MenuItem key={label} value={String(value)}>{label}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              sx={inputSx}
            >
              <MenuItem value="">Todo rol</MenuItem>
              {Object.keys(ROLES).map((role) => (
                <MenuItem key={role} value={role}>{getRoleLabel(role)}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              sx={inputSx}
            >
              <MenuItem value="">Todo estado</MenuItem>
              <MenuItem value="active">Activos</MenuItem>
              <MenuItem value="inactive">Inactivos</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={6} sm={4} md="auto">
            <Tooltip title={sortDate === 'asc' ? 'Más antiguos primero' : 'Más recientes primero'}>
              <IconButton
                onClick={() => setSortDate(sortDate === 'asc' ? 'desc' : 'asc')}
                sx={{ border: '1px solid #d0d7de', borderRadius: 2 }}
              >
                {sortDate === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs="auto" sx={{ ml: 'auto' }}>
            <Tooltip title="Restablecer filtros">
              <IconButton onClick={resetFilters} sx={{ border: '1px solid #d0d7de', borderRadius: 2 }}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden', borderRadius: 3, border: '1px solid #e3e8ef' }}>
        {loading && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: '#f5f8ff', color: '#374151', fontWeight: 700, borderBottom: '2px solid #e3e8ef' } }}>
                <TableCell>Usuario</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Profesión</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Fecha de alta</TableCell>
                {hasActions && <TableCell align="right">Acciones</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={hasActions ? 7 : 6} align="center" sx={{ py: 6, border: 0 }}>
                    <Stack alignItems="center" spacing={1} sx={{ color: '#9ca3af' }}>
                      <PersonSearchIcon sx={{ fontSize: 40 }} />
                      <Typography sx={{ color: '#6b7280' }}>No se encontraron usuarios con estos filtros</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ opacity: row.active ? 1 : 0.6, '&:last-child td': { border: 0 } }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: ROLE_AVATAR_COLORS[row.role] || 'grey.500', width: 36, height: 36, fontSize: 14 }}>
                        {getInitials(row.nombre)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }} noWrap>{row.nombre}</Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }} noWrap>@{row.username} · ID {row.id}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#374151' }}>{row.email}</TableCell>
                  <TableCell sx={{ color: '#374151' }}>{getProfesionLabel(row.profesional)}</TableCell>
                  <TableCell>
                    <Chip size="small" color={ROLE_CHIP_COLORS[row.role] || 'default'} label={getRoleLabel(row.role)} sx={{ fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={row.active ? 'success' : 'default'} label={row.active ? 'Activo' : 'Inactivo'} variant={row.active ? 'filled' : 'outlined'} />
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#6b7280' }}>{new Date(row.createdAt).toLocaleDateString()}</TableCell>
                  {hasActions && (
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {canEditUsers && (
                          <Tooltip title="Editar usuario">
                            <IconButton size="small" onClick={() => setEditUser(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canManagePermissions && (
                          <Tooltip title="Gestionar permisos">
                            <IconButton size="small" onClick={() => setPermissionsUser(row)}>
                              <AdminPanelSettingsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canToggleActive && (
                          <Tooltip title={row.active ? 'Dar de baja' : 'Activar'}>
                            <IconButton size="small" onClick={() => toggleUserActive(row)} color={row.active ? 'default' : 'success'}>
                              {row.active ? <ToggleOnIcon fontSize="small" color="success" /> : <ToggleOffIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
        />
      </Paper>

      <UserPermissionsDialog
        user={permissionsUser}
        open={!!permissionsUser}
        onClose={() => setPermissionsUser(null)}
        onSaved={() => { setPermissionsUser(null); fetchUsers(); }}
      />

      <UserFormDialog
        open={formOpen}
        user={editUser}
        onClose={closeForm}
        onSaved={() => { closeForm(); fetchUsers(); }}
      />
    </Box>
  );
}
